/** Gap between bordered slip sections (screen + print). */
export const SLIP_SECTION_GAP_PX = 8;

/** A4 printable area in CSS px (96dpi): 210×297mm minus 8mm top/bottom, 10mm left/right margins. */
const A4_CONTENT_WIDTH_PX = Math.floor((210 - 20) * 3.7795275591);
const A4_CONTENT_HEIGHT_PX = Math.floor((297 - 16) * 3.7795275591);

/** Shared flex A4 layout (matches App.css .slip-a4-page rules). */
function getSlipA4LayoutCss() {
  const gap = SLIP_SECTION_GAP_PX;
  return `
    .slip-a4-page {
      width: 100% !important;
      height: ${A4_CONTENT_HEIGHT_PX}px !important;
      min-height: ${A4_CONTENT_HEIGHT_PX}px !important;
      max-height: ${A4_CONTENT_HEIGHT_PX}px !important;
      box-sizing: border-box !important;
      display: flex !important;
      flex-direction: column !important;
      gap: ${gap}px !important;
      overflow: hidden !important;
    }
    .slip-a4-page--servicebon {
      padding: 14px 22px 18px !important;
      font-size: 11px !important;
    }
    .slip-a4-page--opleverbon {
      padding: 12px 20px !important;
    }
    .slip-a4-fixed { flex-shrink: 0 !important; }
    .slip-a4-header { margin: 0 !important; }
    .slip-a4-block,
    .slip-work-section,
    .slip-a4-expand-middle,
    .slip-work-section-box,
    .slip-materials-section-box,
    .slip-print-footer-block {
      margin: 0 !important;
    }
    .slip-a4-block,
    .slip-work-section-box,
    .slip-materials-section-box,
    .slip-print-footer-block {
      border: 2px solid #2b2b2b !important;
      box-sizing: border-box !important;
    }
    .slip-a4-expand-middle,
    .slip-work-section {
      flex: 1 1 auto !important;
      min-height: 0 !important;
      display: flex !important;
      flex-direction: column !important;
      gap: ${gap}px !important;
    }
    .slip-a4-expand-middle .slip-work-section { flex: 1.35 1 0 !important; }
    .slip-materials-section {
      flex: 1 1 0 !important;
      min-height: 0 !important;
      display: flex !important;
      flex-direction: column !important;
    }
    .slip-work-section-box,
    .slip-materials-section-box {
      flex: 1 !important;
      min-height: 0 !important;
      display: flex !important;
      flex-direction: column !important;
    }
    .slip-work-section-body,
    .slip-materials-section-body,
    .slip-print-work-body,
    .servicebon-print-fill {
      flex: 1 1 auto !important;
      min-height: 0 !important;
      display: flex !important;
      flex-direction: column !important;
    }
    .slip-print-footer-block { flex-shrink: 0 !important; }
    .slip-footer-fields {
      display: flex !important;
      flex-direction: column !important;
    }
    .slip-footer-field-row {
      flex-shrink: 0 !important;
    }
  `;
}



/**

 * Print stylesheet for Opleverbon / Servicebon — fixed A4, independent of monitor size.

 */

export function getSlipPrintStyles(isServicebon) {

  const workFontSize = isServicebon ? '11px' : '9.5px';

  const workLineHeight = isServicebon ? '1.35' : '1.22';



  return `

    @page {

      size: A4 portrait;

      margin: 8mm 10mm;

    }

    * { box-sizing: border-box; }

    html, body {

      width: ${A4_CONTENT_WIDTH_PX}px;

      max-width: ${A4_CONTENT_WIDTH_PX}px;

      margin: 0;

      padding: 0;

      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;

      color: #1a1a1a;

      line-height: 1.35;

      -webkit-print-color-adjust: exact;

      print-color-adjust: exact;

    }

    table { border-collapse: collapse; }

    .no-print,

    .servicebon-work-draft-field,

    .servicebon-voice-fab,

    .servicebon-voice-status,

    .slip-work-editor-chrome {

      display: none !important;

    }

    .a4-print-shell {

      width: ${A4_CONTENT_WIDTH_PX}px;

      max-width: ${A4_CONTENT_WIDTH_PX}px;

      height: ${A4_CONTENT_HEIGHT_PX}px;

      margin: 0;

      overflow: hidden;

      page-break-inside: avoid;

      break-inside: avoid;

    }

    .slip-print-root {

      width: 100% !important;

      max-width: 100% !important;

      page-break-inside: avoid;

      break-inside: avoid;

    }

    ${getSlipA4LayoutCss()}

    .servicebon-print-fill {

      display: flex !important;

      white-space: pre-wrap;

      font-size: ${workFontSize};

      line-height: ${workLineHeight};

      border: none !important;

      padding: 4px 0 0 0 !important;

      font-style: italic;

      background: transparent !important;

    }

    .slip-print-footer-block,

    .slip-print-signature-row {

      page-break-inside: avoid;

      break-inside: avoid;

    }

    @media print {

      html, body {

        width: ${A4_CONTENT_WIDTH_PX}px;

        height: ${A4_CONTENT_HEIGHT_PX}px;

        overflow: hidden;

      }

      .a4-print-shell {

        page-break-after: avoid;

        page-break-before: avoid;

      }

    }

  `;

}



/** Shrink slip content to fit one A4 page when declaration/work text is very long. */

export function fitSlipPrintToA4(printWindow) {

  const doc = printWindow.document;

  const shell = doc.querySelector('.a4-print-shell');

  const root = doc.querySelector('.slip-print-root');

  if (!shell || !root) return;



  shell.style.width = `${A4_CONTENT_WIDTH_PX}px`;

  root.style.width = '100%';



  const contentHeight = root.getBoundingClientRect().height || root.scrollHeight;

  if (contentHeight <= A4_CONTENT_HEIGHT_PX) return;



  const scale = A4_CONTENT_HEIGHT_PX / contentHeight;

  root.style.transform = `scale(${scale})`;

  root.style.transformOrigin = 'top left';

  shell.style.height = `${A4_CONTENT_HEIGHT_PX}px`;

  shell.style.width = `${A4_CONTENT_WIDTH_PX}px`;

  doc.body.style.height = `${A4_CONTENT_HEIGHT_PX}px`;

  doc.body.style.overflow = 'hidden';

}



function waitForPrintImages(doc) {

  const images = Array.from(doc.images || []);

  if (images.length === 0) return Promise.resolve();

  return Promise.all(

    images.map(

      (img) =>

        new Promise((resolve) => {

          if (img.complete) {

            resolve();

            return;

          }

          img.addEventListener('load', resolve, { once: true });

          img.addEventListener('error', resolve, { once: true });

        }),

    ),

  );

}



export function openSlipPrintWindow({ title, html, isServicebon, onReady }) {

  const printWindow = window.open('', '_blank');

  if (!printWindow) return null;



  const safeTitle = String(title || 'Print').replace(/</g, '');

  printWindow.document.write(`

    <!DOCTYPE html>

    <html>

    <head>

      <meta charset="utf-8" />

      <title>${safeTitle}</title>

      <style>${getSlipPrintStyles(isServicebon)}</style>

    </head>

    <body>

      <div class="a4-print-shell">

        ${html}

      </div>

    </body>

    </html>

  `);

  printWindow.document.close();



  const runPrint = async () => {

    await waitForPrintImages(printWindow.document);

    fitSlipPrintToA4(printWindow);

    if (typeof onReady === 'function') onReady(printWindow);

    printWindow.focus();

    printWindow.print();

    printWindow.close();

  };



  if (printWindow.document.readyState === 'complete') {

    setTimeout(() => void runPrint(), 100);

  } else {

    printWindow.addEventListener('load', () => setTimeout(() => void runPrint(), 100));

  }



  return printWindow;

}


